/**
 * Coach Compliance Dashboard
 *
 * Kiley's mentor view into her sisters' last 7 days. Aggregates each
 * mentee's macro adherence, prayer engagement, welcome-journey progress,
 * and last-log recency into a single warm, scrollable read.
 *
 * Grace-paced design choices:
 *   - "Compliance" framing is internal language only. UI says "tending"
 *     and "this week" so it never feels like surveillance.
 *   - Sisters who are resting (Sabbath or grace-paused) surface in their
 *     own band, not under "needs love", so Kiley can honor the rest.
 *   - The roster is sorted with "needs love" first so Kiley meets the
 *     quietest sister at the top of the page.
 *   - Adherence is computed against each sister's own plan, never
 *     compared across sisters. No leaderboard.
 */
import { Link } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, PillTag, Btn, Avatar, SectionTitle } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import SeasonChip from "@/components/mnf/SeasonChip";
import { useStoreQuery, queries } from "@/lib/store";

/* ---------------------------- Helpers ---------------------------- */

function relativeTime(ts: number | null): string {
  if (!ts) return "no log yet";
  const diff = Date.now() - ts;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d === 1) return "yesterday";
  if (d < 7) return `${d}d ago`;
  return `${Math.floor(d / 7)}w ago`;
}

type Status = "thriving" | "steady" | "needs love";

const statusStyle: Record<Status, { bg: string; color: string; dot: string; label: string }> = {
  "thriving":   { bg: "rgba(168,212,184,0.22)", color: "#2C6248",      dot: "#2C6248",        label: "Thriving" },
  "steady":     { bg: "rgba(30,74,53,0.08)",    color: "var(--mnf-f)", dot: "var(--mnf-f)",   label: "Steady" },
  "needs love": { bg: "rgba(192,120,74,0.16)",  color: "var(--mnf-t)", dot: "var(--mnf-t)",   label: "Needs love" },
};

/** Soft adherence band the bar uses. Mirrors the on-target window of
 *  85 to 115 percent so visuals match the data definition. */
function adherenceColor(pct: number): { bar: string; bg: string } {
  if (pct === 0) return { bar: "rgba(30,74,53,0.18)", bg: "rgba(30,74,53,0.06)" };
  if (pct >= 85 && pct <= 115) return { bar: "var(--mnf-fiber)", bg: "rgba(168,212,184,0.18)" };
  if (pct < 85) return { bar: "var(--mnf-t)", bg: "rgba(192,120,74,0.14)" };
  return { bar: "var(--mnf-gold)", bg: "rgba(160,120,32,0.12)" };
}

/* ---------------------------- Top KPI strip ---------------------------- */

function SummaryStrip({ coachId }: { coachId: string }) {
  const summary = useStoreQuery(() => queries.coachComplianceSummary(coachId));
  if (!summary) return null;
  return (
    <div className="grid grid-cols-2 gap-3" data-testid="strip-coach-summary">
      <GlassCard organic padding={14}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-fiber-deep, #2C6248)" }}>Tending well</p>
        <p
          className="mnf-serif"
          style={{ fontSize: 28, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1, marginTop: 4 }}
          data-testid="text-thriving-count"
        >
          {summary.thriving}
          <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginLeft: 4, fontWeight: 500 }}>
            of {summary.total}
          </span>
        </p>
        <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.4 }}>
          logged 6+ days and lifted a sister in prayer
        </p>
      </GlassCard>
      <GlassCard organic padding={14}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-t)" }}>Need a gentle word</p>
        <p
          className="mnf-serif"
          style={{ fontSize: 28, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1, marginTop: 4 }}
          data-testid="text-needs-love-count"
        >
          {summary.needsLove}
          <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginLeft: 4, fontWeight: 500 }}>
            of {summary.total}
          </span>
        </p>
        <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.4 }}>
          quiet for 2+ days or under 3 logs this week
        </p>
      </GlassCard>
      <GlassCard organic padding={14}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)" }}>Roster log rate</p>
        <p
          className="mnf-serif"
          style={{ fontSize: 28, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1, marginTop: 4 }}
          data-testid="text-log-rate"
        >
          {summary.rosterLogRate}
          <span className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", marginLeft: 2, fontWeight: 500 }}>%</span>
        </p>
        <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.4 }}>
          {summary.total * 7} possible meal-log days, {Math.round((summary.rosterLogRate / 100) * summary.total * 7)} filled
        </p>
      </GlassCard>
      <GlassCard organic padding={14}>
        <p className="mnf-eyebrow" style={{ color: "var(--mnf-coral, var(--mnf-t))" }}>Prayer engagement</p>
        <p
          className="mnf-serif"
          style={{ fontSize: 28, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1, marginTop: 4 }}
          data-testid="text-prayer-engagement"
        >
          {summary.totalPrayerEngagement}
        </p>
        <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.4 }}>
          posts and lifts across your roster, last 7 days
        </p>
      </GlassCard>
    </div>
  );
}

/* ---------------------------- Mentee row ---------------------------- */

interface RowProps {
  menteeId: string;
  isLast: boolean;
}

function MenteeRow({ menteeId, isLast }: RowProps) {
  const adh = useStoreQuery(() => queries.menteeAdherence(menteeId));
  const mentees = useStoreQuery(() => queries.menteesForCoach("u_kiley"));
  const m = mentees?.find((x) => x.id === menteeId);
  if (!adh || !m) return null;
  const status: Status = adh.derivedStatus;
  const palette = statusStyle[status];
  const slug = menteeId.replace(/^u_/, "");
  const pct = Math.min(125, adh.avgKcalPctOfTarget);
  const barColors = adherenceColor(adh.avgKcalPctOfTarget);

  /* Shape a one-line warm summary so Kiley can scan the row without
   * opening the macros sheet. The phrasing avoids brand-banned em-dashes. */
  let warmLine = "";
  if (adh.resting) {
    warmLine = `Resting day ${adh.restingDays}, holding her gently.`;
  } else if (adh.daysLogged === 0) {
    warmLine = "No logs this week. Maybe a check-in note?";
  } else if (adh.daysLogged >= 6 && adh.onTargetDays >= 5) {
    warmLine = `${adh.onTargetDays} of ${adh.daysLogged} days on target. Send a celebration.`;
  } else if (status === "needs love") {
    warmLine = `Last log ${relativeTime(adh.lastMealLogAt)}. A short voice note would land.`;
  } else {
    warmLine = `${adh.daysLogged} of 7 days logged, ${adh.onTargetDays} on target.`;
  }

  return (
    <div
      className="p-4 transition-all"
      style={{ borderBottom: !isLast ? "1px solid rgba(30,74,53,0.06)" : undefined }}
      data-testid={`compliance-row-${slug}`}
    >
      <div className="flex items-start gap-3">
        <Avatar initials={m.initials} size={44} ringColor="rgba(127,119,221,0.55)" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="mnf-body truncate" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--mnf-fd)" }}>
              {m.name}
            </p>
            <span
              className="mnf-body"
              style={{
                padding: "2px 8px", borderRadius: 999,
                background: palette.bg, color: palette.color,
                fontSize: 9.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
              }}
              data-testid={`status-pill-${slug}`}
            >
              {palette.label}
            </span>
            {adh.resting && <PillTag tone="purple">Resting</PillTag>}
            {adh.season && adh.season.kind !== "none" && (
              <SeasonChip season={adh.season} size="sm" testId={`chip-compliance-season-${slug}`} />
            )}
          </div>
          <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 4, lineHeight: 1.45 }}>
            {warmLine}
          </p>

          {/* Adherence bar */}
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="mnf-eyebrow" style={{ color: "var(--mnf-tg)" }}>Avg kcal vs plan</span>
              <span className="mnf-body" style={{ fontSize: 10.5, color: "var(--mnf-fd)", fontWeight: 700 }}>
                {adh.daysLogged === 0 ? "no data" : `${adh.avgKcalPctOfTarget}%`}
              </span>
            </div>
            <div
              style={{ height: 7, borderRadius: 999, background: barColors.bg, position: "relative", overflow: "hidden" }}
              data-testid={`bar-adherence-${slug}`}
            >
              <div
                style={{
                  width: `${(pct / 125) * 100}%`,
                  height: "100%",
                  background: barColors.bar,
                  borderRadius: 999,
                  transition: "width 600ms ease",
                }}
              />
              {/* Target band marker (85 to 115) */}
              <div
                style={{
                  position: "absolute", top: -2, bottom: -2,
                  left: `${(85 / 125) * 100}%`, width: `${((115 - 85) / 125) * 100}%`,
                  border: "1px dashed rgba(30,74,53,0.2)", borderRadius: 4, pointerEvents: "none",
                }}
                aria-hidden
              />
            </div>
          </div>

          {/* Stat chips */}
          <div className="flex items-center gap-1.5 flex-wrap mt-3">
            <span className="inline-flex items-center gap-1" style={{
              padding: "3px 8px", borderRadius: 999,
              background: "rgba(168,212,184,0.18)", color: "var(--mnf-f)",
            }}>
              <Icon name="restaurant" size={11} />
              <span className="mnf-body" style={{ fontSize: 10, fontWeight: 700 }}>{adh.daysLogged}/7 logged</span>
            </span>
            <span className="inline-flex items-center gap-1" style={{
              padding: "3px 8px", borderRadius: 999,
              background: "rgba(127,119,221,0.16)", color: "var(--mnf-purple)",
            }}>
              <Icon name="favorite" size={11} />
              <span className="mnf-body" style={{ fontSize: 10, fontWeight: 700 }}>
                {adh.prayerLiftCount7d + adh.prayerPostCount7d} prayer
              </span>
            </span>
            <span className="inline-flex items-center gap-1" style={{
              padding: "3px 8px", borderRadius: 999,
              background: "rgba(160,120,32,0.14)", color: "var(--mnf-gold)",
            }}>
              <Icon name="local_fire_department" size={11} />
              <span className="mnf-body" style={{ fontSize: 10, fontWeight: 700 }}>{adh.streak}d streak</span>
            </span>
            {!adh.welcomeComplete && adh.welcomeDay > 0 && (
              <span className="inline-flex items-center gap-1" style={{
                padding: "3px 8px", borderRadius: 999,
                background: "rgba(192,120,74,0.14)", color: "var(--mnf-t)",
              }}>
                <Icon name="auto_awesome" size={11} />
                <span className="mnf-body" style={{ fontSize: 10, fontWeight: 700 }}>Welcome day {adh.welcomeDay}</span>
              </span>
            )}
            <span className="mnf-body" style={{ fontSize: 10, color: "var(--mnf-tg)", marginLeft: 2 }}>
              · last log {relativeTime(adh.lastMealLogAt)}
            </span>
          </div>

          {/* Action row */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <Link href={`/chat/${slug}`} className="no-underline" data-testid={`button-compliance-message-${slug}`}>
              <Btn variant="primary" size="sm">
                <Icon name="chat_bubble" size={12} /> Send a word
              </Btn>
            </Link>
            <Link href={`/coach/macros/${menteeId}`} className="no-underline" data-testid={`button-compliance-macros-${slug}`}>
              <Btn variant="ghost" size="sm">
                <Icon name="local_fire_department" size={12} /> Open macros
              </Btn>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------- Page ---------------------------- */

export default function CoachCompliance() {
  const summary = useStoreQuery(() => queries.coachComplianceSummary("u_kiley"));
  const rows = useStoreQuery(() => queries.menteeComplianceRows("u_kiley"));

  if (!summary || !rows) {
    return (
      <div className="pb-6">
        <ScreenHeader variant="forest" eyebrow="Coach view" title="This week with your sisters" />
      </div>
    );
  }

  const needsLoveRows = rows.filter((r) => r.adh.derivedStatus === "needs love" && !r.adh.resting);
  const restingRows = rows.filter((r) => r.adh.resting);
  const thrivingRows = rows.filter((r) => r.adh.derivedStatus === "thriving");

  return (
    <div className="pb-6 flex flex-col gap-4">
      <ScreenHeader
        variant="forest"
        eyebrow="Coach view"
        title={<>This week<br /><span className="italic">with your sisters</span></>}
        subtitle={
          summary.needsLove === 0
            ? "Every sister has touched her plate this week. Praise the Lord."
            : `${summary.needsLove} of ${summary.total} sisters could use a gentle word from you today.`
        }
        trailing={<Avatar initials="K" size={38} ringColor="rgba(229,185,151,0.65)" />}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <PillTag tone="fiber">{summary.thriving} tending well</PillTag>
          <PillTag tone="coral">{summary.needsLove} need love</PillTag>
          <PillTag tone="purple">{summary.resting} resting</PillTag>
          <Link href="/coach" className="no-underline" data-testid="link-coach-roster">
            <span
              className="inline-flex items-center gap-1"
              style={{
                padding: "5px 10px", borderRadius: 999,
                background: "rgba(245,240,232,0.18)", color: "#F5F0E8",
                fontSize: 10.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                cursor: "pointer",
              }}
            >
              Full roster <Icon name="chevron_right" size={12} />
            </span>
          </Link>
        </div>
      </ScreenHeader>

      <div style={{ padding: "0 16px" }} className="flex flex-col gap-5">
        {/* Scripture for the coach */}
        <GlassCard organic padding={18} data-testid="card-coach-scripture">
          <div className="flex items-start gap-3">
            <span style={{ fontSize: 26, lineHeight: 1 }} aria-hidden>🌿</span>
            <div className="flex-1">
              <p className="mnf-eyebrow" style={{ color: "var(--mnf-purple)" }}>For the shepherdess</p>
              <p
                className="mnf-serif italic"
                style={{ fontSize: 15, fontWeight: 500, color: "var(--mnf-fd)", lineHeight: 1.5, marginTop: 6 }}
              >
                "Be sure you know the condition of your flocks, give careful attention to your herds."
              </p>
              <p className="mnf-body" style={{ fontSize: 11, color: "var(--mnf-tg)", marginTop: 6, fontWeight: 700 }}>
                Proverbs 27:23
              </p>
            </div>
          </div>
        </GlassCard>

        {/* Summary KPI strip */}
        <SummaryStrip coachId="u_kiley" />

        {/* Needs love (priority) */}
        {needsLoveRows.length > 0 && (
          <div>
            <SectionTitle
              eyebrow="Tend first"
              title="A gentle word today"
              trailing={<span style={{ color: "var(--mnf-t)" }}>{needsLoveRows.length}</span>}
            />
            <GlassCard padding={0} style={{ overflow: "hidden" }} data-testid="card-needs-love-list">
              {needsLoveRows.map((r, i) => (
                <MenteeRow
                  key={r.mentee.id}
                  menteeId={r.mentee.id}
                  isLast={i === needsLoveRows.length - 1}
                />
              ))}
            </GlassCard>
          </div>
        )}

        {/* Resting sisters */}
        {restingRows.length > 0 && (
          <div>
            <SectionTitle
              eyebrow="Honoring rest"
              title="Sisters in Sabbath"
              trailing={<span style={{ color: "var(--mnf-purple)" }}>{restingRows.length}</span>}
            />
            <GlassCard padding={0} style={{ overflow: "hidden" }} data-testid="card-resting-list">
              {restingRows.map((r, i) => (
                <MenteeRow
                  key={r.mentee.id}
                  menteeId={r.mentee.id}
                  isLast={i === restingRows.length - 1}
                />
              ))}
            </GlassCard>
            <p
              className="mnf-body italic"
              style={{ fontSize: 11.5, color: "var(--mnf-tg)", marginTop: 8, textAlign: "center", lineHeight: 1.5 }}
            >
              These sisters are resting on purpose. Their streaks are pausing, not breaking.
            </p>
          </div>
        )}

        {/* Thriving celebration */}
        {thrivingRows.length > 0 && (
          <div>
            <SectionTitle
              eyebrow="Cheer on"
              title="Tending well this week"
              trailing={<span style={{ color: "var(--mnf-fiber-deep, #2C6248)" }}>{thrivingRows.length}</span>}
            />
            <GlassCard padding={0} style={{ overflow: "hidden" }} data-testid="card-thriving-list">
              {thrivingRows.map((r, i) => (
                <MenteeRow
                  key={r.mentee.id}
                  menteeId={r.mentee.id}
                  isLast={i === thrivingRows.length - 1}
                />
              ))}
            </GlassCard>
          </div>
        )}

        {/* All sisters (full roster, sorted by needs-love-first) */}
        <div>
          <SectionTitle
            eyebrow="The whole garden"
            title="All sisters"
            trailing={<span style={{ color: "var(--mnf-tg)" }}>{rows.length}</span>}
          />
          <GlassCard padding={0} style={{ overflow: "hidden" }} data-testid="card-all-sisters-list">
            {rows.map((r, i) => (
              <MenteeRow
                key={r.mentee.id}
                menteeId={r.mentee.id}
                isLast={i === rows.length - 1}
              />
            ))}
          </GlassCard>
        </div>

        {/* Footer disclaimer */}
        <GlassCard organic padding={14} data-testid="card-compliance-footer">
          <p
            className="mnf-body italic"
            style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.55, textAlign: "center" }}
          >
            "Compliance" here means tending, not surveilling. Every adherence number compares a
            sister to her own plan, never to another sister. Macros are wellness guidance, not
            medical prescriptions. Always defer to a sister's OB or RD.
          </p>
        </GlassCard>
      </div>
    </div>
  );
}
